/**
 * Support Chat Widget
 * Floating chat bubble for authenticated employees to contact support
 */

import { useState, useEffect, useRef } from 'react';
import { MessageCircle, X, Send, Loader2, Image as ImageIcon, Check, CheckCheck, Lock } from 'lucide-react';
import { Button } from '../shared/components/ui/Button';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import supportAPI from '../services/supportApi';
import { getAPIBaseURL } from '../services/api';

interface Message {
  id: string;
  sender_type: 'client' | 'support';
  sender_name: string;
  content: string;
  attachment_url: string | null;
  attachment_type: 'image' | 'document' | null;
  created_at: string;
}

interface TicketSession {
  ticketId: string;
  ticketNumber: string;
  clientName: string;
  clientEmail?: string;
}

const STORAGE_KEY = 'stratify_support_ticket_session';
const MAX_ATTACHMENT_SIZE_MB = 10;
const MAX_ATTACHMENT_BYTES = MAX_ATTACHMENT_SIZE_MB * 1024 * 1024;

export function SupportChatWidget() {
  const { user } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const [showHint, setShowHint] = useState(true);
  const [session, setSession] = useState<TicketSession | null>(null);
  const [ticketStatus, setTicketStatus] = useState<string>('open');
  const [messages, setMessages] = useState<Message[]>([]);
  const [newMessage, setNewMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [clientName, setClientName] = useState('');
  const [clientEmail, setClientEmail] = useState('');
  const [initialMessage, setInitialMessage] = useState('');
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [selectedFileName, setSelectedFileName] = useState<string | null>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);

  // Pre-fill form with user data
  useEffect(() => {
    if (user && !session) {
      setClientName(user.displayName || '');
      setClientEmail(user.email || '');
    }
  }, [user, session]);

  // Reset session when user changes
  useEffect(() => {
    if (user) {
      localStorage.removeItem(STORAGE_KEY);
      setSession(null);
      setMessages([]);
      setClientName(user.displayName || '');
      setClientEmail(user.email || '');
    }
  }, [user]);

  // Hide hint when widget is opened
  useEffect(() => {
    if (isOpen) {
      setShowHint(false);
    } else {
      setShowHint(true);
    }
  }, [isOpen]);

  // Load session from localStorage
  useEffect(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved) {
      try {
        setSession(JSON.parse(saved));
      } catch {
        localStorage.removeItem(STORAGE_KEY);
      }
    }
  }, []);

  // Fetch messages when session exists
  useEffect(() => {
    if (!session) return;

    let cancelled = false;

    const fetchMessagesData = async () => {
      try {
        const data = await supportAPI.fetchMessages(session.ticketId);
        if (cancelled) return;
        setMessages(data.messages);
        setTicketStatus(data.status);
      } catch (error) {
        console.error('Failed to fetch messages:', error);
      }
    };

    fetchMessagesData();

    let interval: number | undefined;
    if (isOpen) {
      interval = window.setInterval(fetchMessagesData, 3000);
    }

    return () => {
      cancelled = true;
      if (interval) window.clearInterval(interval);
    };
  }, [session, isOpen]);

  // Auto-scroll to bottom
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > MAX_ATTACHMENT_BYTES) {
      toast.error(`File too large (max ${MAX_ATTACHMENT_SIZE_MB}MB)`);
      e.target.value = '';
      return;
    }
    setSelectedFile(file);
    setSelectedFileName(file.name);
  };

  const clearFileSelection = () => {
    setSelectedFile(null);
    setSelectedFileName(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  /** Base URL for attachment links/images (same origin as API, without /api/v1). */
  const attachmentBaseUrl = getAPIBaseURL().replace(/\/api\/v1\/?$/, '');

  const handleStartChat = async () => {
    if (!clientName.trim() || !initialMessage.trim()) return;

    setIsLoading(true);
    try {
      let attachmentUrl: string | undefined;
      let attachmentType: 'image' | 'document' | undefined;
      if (selectedFile) {
        const uploaded = await supportAPI.uploadAttachment(selectedFile);
        attachmentUrl = uploaded.url;
        attachmentType = uploaded.type;
      }

      const data = await supportAPI.createTicket({
        clientName: clientName.trim(),
        clientEmail: clientEmail.trim() || undefined,
        message: initialMessage.trim(),
        attachmentUrl,
        attachmentType,
      });

      const newSession: TicketSession = {
        ticketId: data.ticketId,
        ticketNumber: data.ticketNumber,
        clientName: clientName.trim(),
        clientEmail: clientEmail.trim() || undefined,
      };

      setSession(newSession);
      localStorage.setItem(STORAGE_KEY, JSON.stringify(newSession));
      setClientName('');
      setClientEmail('');
      setInitialMessage('');
      clearFileSelection();
      toast.success('Chat started successfully');
    } catch (error: any) {
      console.error('Failed to start chat:', error);
      toast.error(error?.response?.data?.error || 'Failed to start chat');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSendMessage = async () => {
    if (!session) return;
    const hasText = newMessage.trim().length > 0;
    const hasFile = Boolean(selectedFile);
    if (!hasText && !hasFile) return;

    setIsLoading(true);
    try {
      let attachmentUrl: string | undefined;
      let attachmentType: 'image' | 'document' | undefined;
      if (selectedFile) {
        const uploaded = await supportAPI.uploadAttachment(selectedFile);
        attachmentUrl = uploaded.url;
        attachmentType = uploaded.type;
      }

      await supportAPI.sendMessage(session.ticketId, {
        message: hasText ? newMessage.trim() : '[Attachment]',
        attachmentUrl,
        attachmentType,
      });

      setNewMessage('');
      clearFileSelection();
    } catch (error: any) {
      console.error('Failed to send message:', error);
      toast.error(error?.response?.data?.error || 'Failed to send message');
    } finally {
      setIsLoading(false);
    }
  };

  const handleNewConversation = () => {
    localStorage.removeItem(STORAGE_KEY);
    setSession(null);
    setMessages([]);
    setTicketStatus('open');
    if (user) {
      setClientName(user.displayName || '');
      setClientEmail(user.email || '');
    }
  };

  const handleCloseTicket = async () => {
    if (!session) return;

    const confirmed = window.confirm(
      'Are you sure you want to close this ticket? You can start a new conversation anytime.'
    );
    if (!confirmed) return;

    setIsLoading(true);
    try {
      await supportAPI.closeTicket(session.ticketId, session.clientName);
      setTicketStatus('closed');
      toast.success('Ticket closed');
    } catch (error) {
      console.error('Failed to close ticket:', error);
      toast.error('Failed to close ticket');
    } finally {
      setIsLoading(false);
    }
  };

  // Don't show if not authenticated
  if (!user) return null;

  return (
    <>
      <input
        ref={fileInputRef}
        type="file"
        className="hidden"
        accept="image/*,.pdf,.doc,.docx,.txt"
        onChange={handleFileSelect}
      />
      {showHint && !isOpen && (
        <div
          className="fixed bottom-24 right-6 z-50 rounded-xl shadow-lg px-4 py-3 max-w-[200px] animate-in fade-in slide-in-from-bottom-2 duration-300"
          style={{ backgroundColor: '#ffffff', border: '1px solid #E5E7EB' }}
        >
          <p className="text-sm font-medium text-gray-900">Need help? Chat here 👋</p>
          <button
            onClick={() => setShowHint(false)}
            className="absolute -top-2 -right-2 bg-gray-100 hover:bg-gray-200 rounded-full p-1"
          >
            <X className="h-3 w-3 text-gray-500" />
          </button>
          <div
            className="absolute -bottom-2 right-6 w-0 h-0"
            style={{
              borderLeft: '8px solid transparent',
              borderRight: '8px solid transparent',
              borderTop: '8px solid #ffffff',
              filter: 'drop-shadow(0 4px 6px rgba(0,0,0,0.1))',
            }}
          />
        </div>
      )}

      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="fixed bottom-6 right-6 z-50 h-14 w-14 rounded-full shadow-lg bg-white hover:bg-white/90 border-[3px] flex items-center justify-center"
        style={{ borderColor: '#16a34a' }}
        aria-label={isOpen ? 'Close support chat' : 'Open support chat'}
      >
        {isOpen ? (
          <X className="h-6 w-6" style={{ color: '#16a34a' }} />
        ) : (
          <MessageCircle className="h-6 w-6" style={{ color: '#16a34a' }} />
        )}
      </button>

      {isOpen && (
        <div className="fixed bottom-24 right-6 z-50 w-[380px] max-w-[calc(100vw-48px)] rounded-xl border shadow-xl" style={{ backgroundColor: '#ffffff' }}>
          <div
            className="flex items-center justify-between px-4 py-3 rounded-t-xl border-b"
            style={{ backgroundColor: '#2563eb', color: '#ffffff', borderColor: '#1d4ed8' }}
          >
            <div>
              <h3 className="font-semibold">Support Chat</h3>
              {session && (
                <div className="flex items-center gap-2">
                  <p className="text-xs opacity-80">Ticket: {session.ticketNumber}</p>
                  {ticketStatus === 'closed' && (
                    <span className="text-xs bg-primary-foreground/20 px-1.5 py-0.5 rounded flex items-center gap-1">
                      <Lock className="h-3 w-3" />
                      Closed
                    </span>
                  )}
                </div>
              )}
            </div>
            {session && (
              <div className="flex gap-1">
                {ticketStatus === 'open' && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={handleCloseTicket}
                    disabled={isLoading}
                    className="text-white hover:text-white/80 hover:bg-blue-600/80"
                  >
                    Close
                  </Button>
                )}
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleNewConversation}
                  className="text-white hover:text-white/80 hover:bg-blue-600/80"
                >
                  New Chat
                </Button>
              </div>
            )}
          </div>

          <div className="h-[400px] flex flex-col" style={{ backgroundColor: '#ffffff' }}>
            {!session ? (
              <>
                <div className="flex-1 min-h-0 overflow-auto p-4 space-y-4" style={{ backgroundColor: '#ffffff' }}>
                  <p className="text-sm text-muted-foreground">
                    Start a conversation with our support team. We typically respond within a few minutes.
                  </p>
                  <div className="space-y-3">
                    <div>
                      <label className="text-sm font-medium mb-1 block">Name *</label>
                      <input
                        value={clientName}
                        onChange={(e) => setClientName(e.target.value)}
                        placeholder="Your name"
                        className="block w-full rounded-xl border border-gray-300 bg-white px-3 py-2 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:border-blue-500"
                      />
                    </div>
                    <div>
                      <label className="text-sm font-medium mb-1 block">Email (optional)</label>
                      <input
                        type="email"
                        value={clientEmail}
                        onChange={(e) => setClientEmail(e.target.value)}
                        placeholder="your@email.com"
                        className="block w-full rounded-xl border border-gray-300 bg-white px-3 py-2 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:border-blue-500"
                      />
                    </div>
                    <div>
                      <label className="text-sm font-medium mb-1 block">Message *</label>
                      <textarea
                        value={initialMessage}
                        onChange={(e) => setInitialMessage(e.target.value)}
                        placeholder="How can we help you?"
                        rows={3}
                        className="block w-full rounded-xl border border-gray-300 bg-white px-3 py-2 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:border-blue-500 resize-vertical"
                      />
                    </div>
                    <div>
                      <button
                        type="button"
                        onClick={() => fileInputRef.current?.click()}
                        className="mt-1 flex w-full items-center justify-center gap-2 rounded-xl border border-gray-300 bg-white px-3 py-2 text-sm shadow-sm hover:bg-gray-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:border-blue-500"
                      >
                        <ImageIcon className="h-4 w-4 text-gray-500" />
                        <span>
                          Attach Screenshot/File{selectedFile ? ` (1 file)` : ''}
                        </span>
                      </button>
                      {selectedFileName && (
                        <div className="mt-2 p-2 bg-gray-50 rounded flex items-center justify-between">
                          <span className="text-xs truncate flex-1">{selectedFileName}</span>
                          <button
                            type="button"
                            onClick={clearFileSelection}
                            className="h-6 w-6 inline-flex items-center justify-center rounded-full hover:bg-gray-200"
                          >
                            <X className="h-3 w-3 text-gray-500" />
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
                <div className="shrink-0 border-t p-3" style={{ backgroundColor: '#ffffff' }}>
                  <Button
                    onClick={handleStartChat}
                    disabled={!clientName.trim() || !initialMessage.trim() || isLoading}
                    className="w-full rounded-xl bg-blue-500 text-white text-sm font-medium py-2.5 shadow-sm hover:bg-blue-600 disabled:bg-gray-200 disabled:text-gray-400 disabled:cursor-not-allowed"
                  >
                    {isLoading ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Starting...
                      </>
                    ) : (
                      'Start Chat'
                    )}
                  </Button>
                </div>
              </>
            ) : (
              <>
                <div className="flex-1 overflow-auto p-4 space-y-3" style={{ backgroundColor: '#ffffff' }}>
                  {messages.length === 0 ? (
                    <p className="text-sm text-muted-foreground text-center py-8">
                      Waiting for support response...
                    </p>
                  ) : (
                    messages.map((msg, index) => {
                      const hasBeenRead =
                        msg.sender_type === 'client' &&
                        messages.slice(index + 1).some((m) => m.sender_type === 'support');

                      return (
                        <div
                          key={msg.id}
                          className={`flex flex-col ${
                            msg.sender_type === 'client' ? 'items-end' : 'items-start'
                          }`}
                        >
                          <div
                            className={`max-w-[80%] rounded-lg px-3 py-2 ${
                              msg.sender_type === 'client'
                                ? 'bg-primary text-primary-foreground'
                                : 'bg-muted'
                            }`}
                          >
                            {msg.sender_type === 'support' && (
                              <p className="text-xs font-medium opacity-70 mb-1">{msg.sender_name}</p>
                            )}
                            <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
                            {msg.attachment_url && (
                              <div className="mt-2">
                                {msg.attachment_type === 'image' ? (
                                  <a
                                    href={`${attachmentBaseUrl}${msg.attachment_url}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="block rounded overflow-hidden border border-black/10 max-w-[200px]"
                                  >
                                    <img
                                      src={`${attachmentBaseUrl}${msg.attachment_url}`}
                                      alt="Attachment"
                                      className="max-h-40 w-auto object-contain"
                                    />
                                  </a>
                                ) : (
                                  <a
                                    href={`${attachmentBaseUrl}${msg.attachment_url}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-xs underline opacity-90"
                                  >
                                    View attachment
                                  </a>
                                )}
                              </div>
                            )}
                          </div>
                          {msg.sender_type === 'client' && (
                            <div className="flex items-center gap-1 mt-0.5 text-xs text-muted-foreground">
                              {hasBeenRead ? (
                                <>
                                  <CheckCheck className="h-3 w-3 text-primary" />
                                  <span>Seen</span>
                                </>
                              ) : (
                                <>
                                  <Check className="h-3 w-3" />
                                  <span>Delivered</span>
                                </>
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })
                  )}
                  <div ref={messagesEndRef} />
                </div>

                {ticketStatus === 'closed' ? (
                  <div className="border-t p-3 text-center text-sm text-muted-foreground" style={{ backgroundColor: '#ffffff' }}>
                    <Lock className="h-4 w-4 inline-block mr-1" />
                    This ticket is closed. Start a new chat to continue.
                  </div>
                ) : (
                  <div className="border-t p-3 flex flex-col gap-2" style={{ backgroundColor: '#ffffff' }}>
                    {selectedFileName && (
                      <div className="flex items-center justify-between text-xs text-muted-foreground bg-gray-50 rounded-lg px-2 py-1">
                        <span className="truncate flex-1">{selectedFileName}</span>
                        <button type="button" onClick={clearFileSelection} className="shrink-0 p-1 hover:bg-gray-200 rounded">
                          <X className="h-3 w-3" />
                        </button>
                      </div>
                    )}
                    <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => fileInputRef.current?.click()}
                      className="relative flex h-10 items-center gap-1.5 rounded-lg border border-gray-300 bg-white px-2.5 text-gray-500 hover:bg-gray-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:border-blue-500"
                      aria-label={selectedFile ? '1 file attached' : 'Attach file'}
                      title={selectedFile ? '1 file attached' : 'Attach file'}
                    >
                      <ImageIcon className="h-4 w-4 shrink-0" />
                      <span className="text-xs font-medium hidden sm:inline">
                        {selectedFile ? 'Attach (1 file)' : 'Attach'}
                      </span>
                      {selectedFile && (
                        <span className="sm:hidden absolute -top-1 -right-1 flex h-4 w-4 items-center justify-center rounded-full bg-blue-500 text-[10px] font-medium text-white">
                          1
                        </span>
                      )}
                    </button>
                    <input
                      value={newMessage}
                      onChange={(e) => setNewMessage(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && !e.shiftKey) {
                          e.preventDefault();
                          handleSendMessage();
                        }
                      }}
                      placeholder="Type a message..."
                      disabled={isLoading}
                      className="block w-full rounded-xl border border-gray-300 bg-white px-3 py-2 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:border-blue-500"
                    />
                    <button
                      type="button"
                      onClick={handleSendMessage}
                      disabled={(!newMessage.trim() && !selectedFile) || isLoading}
                      className="h-10 w-10 flex items-center justify-center rounded-full bg-blue-500 text-white shadow-sm hover:bg-blue-600 disabled:bg-gray-200 disabled:text-gray-400 disabled:cursor-not-allowed"
                      aria-label="Send message"
                    >
                      {isLoading ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <Send className="h-4 w-4" />
                      )}
                    </button>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      )}
    </>
  );
}

export default SupportChatWidget;
